import { useEffect, useRef } from 'react';

/** How often a live page re-asks for its data while it is open and visible. */
export const LIVE_REFRESH_MS = 60000;

type Refetch = () => unknown;

/**
 * Keeps a race-day page current without a reload: while `active` and the tab
 * is visible, calls each refetch every minute, and straight away when the tab
 * becomes visible again (someone coming back after a race sees the result at
 * once). A hidden tab does nothing. Apollo refetches are silent by default
 * (no loading state), so the page just updates in place.
 */
export function useLiveRefresh(active: boolean, refetches: Refetch[], ms = LIVE_REFRESH_MS): void {
  // the latest refetch functions, without restarting the timer on every render
  const fns = useRef(refetches);
  fns.current = refetches;

  useEffect(() => {
    if (!active) return;
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      for (const f of fns.current) {
        try {
          const p = f() as Promise<unknown> | undefined;
          // a failed poll is not worth surfacing: the next one tries again
          p?.catch?.(() => undefined);
        } catch {
          // ponytail: a refetch that throws synchronously is skipped this round
        }
      }
    };
    const id = setInterval(refresh, ms);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, ms]);
}
